import {inject, ref} from 'vue';
import {defineStore} from 'pinia';
import dayjs from 'dayjs';
import {useApiHook, apiError} from "@/composition/apiHook";

export const useAppStore = defineStore('app', () => {

    const {standard} = useApiHook();

    let eventSource;

    const connect = () => {
        eventSource = new EventSource('/sse');
        eventSource.onopen = onOpen;
        eventSource.onmessage = onMessage;
        eventSource.onerror = onError;

        Promise.all([
            loadDevices(),
            loadUsers(),
            loadRemocons(),
            // loadSchedules(),
            loadTasks(),
            loadPosts(),
            // loadLogs(),
            loadLinks(),
            // loadTelegramChatIds(),
        ]).then();
    };

    const disconnect = () => {
        if (eventSource) {
            console.log('[SSE] 🧹 SSE 연결 종료');
            eventSource.close();
        }
    };

    const onOpen = () => {
        console.log('[SSE] Connected');
    };

    const onMessage = event => {
        console.log('[SSE] Message:', event.data);
        const message = JSON.parse(event.data);

        (async () => {
            if (message.type === 'device')
                await loadDevices();
            else if (message.type === 'user')
                await loadUsers();
            else if (message.type === 'remocon')
                await loadRemocons();
            else if (message.type === 'task')
                await loadTasks();
            else if (message.type === 'post')
                await loadPosts();
        })();
    };

    const onError = error => {
        console.error('[SSE] Error:', error);
    };

    // WebSocket
    const socket = ref(null);
    const isSocketConnected = ref(false);
    const socketMessages = ref([]);

    const connectWs = () => {
        if (socket.value && socket.value.readyState === WebSocket.OPEN) return;

        const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
        const url = `${protocol}://${location.host}/ws`;
        socket.value = new WebSocket(url);

        socket.value.onopen = onOpenWs;
        socket.value.onmessage = onMessageWs;
        socket.value.onclose = onCloseWs;
        socket.value.onerror = onErrorWs;
    };

    const disconnectWs = () => {
        if (!socket.value) return;
        socket.value.close();
        socket.value = null;
        isSocketConnected.value = false;
    };

    const sendWs = data => {
        if (socket.value && socket.value.readyState === WebSocket.OPEN) {
            socket.value.send(typeof data === 'string' ? data : JSON.stringify(data));
        } else {
            console.warn('[WebSocket] Not connected, cannot send');
        }
    };

    const onOpenWs = () => {
        isSocketConnected.value = true;
        console.log('[WebSocket] Connected');

        Promise.all([
            loadDevices(),
            loadUsers(),
            loadRemocons(),
            // loadSchedules(),
            loadTasks(),
            loadPosts(),
            // loadLogs(),
            // loadLinks(),
            // loadTelegramChatIds(),
        ]).then();
    };

    const onMessageWs = event => {
        console.log('[WebSocket] Message:', event.data);
        socketMessages.value.push(event.data);

        // 예: 메시지를 로그에 추가하는 경우
        // logs.value.push(JSON.parse(event.data));
    };

    const onCloseWs = () => {
        isSocketConnected.value = false;
        console.log('[WebSocket] Disconnected');
    };

    const onErrorWs = error => {
        console.error('[WebSocket] Error:', error);
    };

    // 데이터
    const axios = inject('axios');
    const network = inject('network');
    const devices = ref([]);
    const users = ref([]);
    const remocons = ref([]);
    const schedules = ref([]);
    const tasks = ref([]);
    const posts = ref([]);
    const logs = ref([]);
    const links = ref([]);
    const telegramChatIds = ref([]);

    const loadDevices = async () => {
        try {
            const response = await axios.get(`/api/device`);
            if (await apiError(response, standard)) return;

            devices.value = response.data.response;

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadUsers = async () => {
        try {
            const response = await axios.get('/api/user');
            if (await apiError(response, standard)) return;

            users.value = response.data.response;

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadRemocons = async () => {
        try {
            const response = await axios.get(`/api/remocon`);
            if (await apiError(response, standard)) return;

            remocons.value = response.data.response.map(remocon => ({
                ...remocon,
                buttons: remocon.buttons.map(({id, name, description, stepsJson}) => ({
                    id, name, description,
                    steps: stepsJson && JSON.parse(stepsJson) || [],
                })),
            }));

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadSchedules = async () => {
        try {
            const response = await axios.get(`/api/schedule`);
            if (!response || 200 !== response.status || 0 !== response.data.code) return;

            schedules.value = response.data.response;

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadTasks = async () => {
        try {
            const response = await axios.get('/api/task');
            if (await apiError(response, standard)) return;

            tasks.value = response.data.response;

            return response;

        } catch (error) {
            console.log(error);
        }
    }

    const loadPosts = async () => {
        try {
            const response = await axios.get('/api/post');
            if (await apiError(response, standard)) return;

            posts.value = response.data.response.map(post => ({
                ...post,
                attachments: post.attachments.map(attachment => ({
                    ...attachment,
                    // uuid: uuid.v1(),
                    src: `/${attachment.path}`,
                })),
            }));

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadLogs = async () => {
        const [lastLog] = logs.value.slice(-1);
        const updateTime = lastLog
            ? dayjs(lastLog.createdAt).format('YYYY-MM-DD HH:mm:ss.SSS')
            : dayjs().subtract(1, 'week').format('YYYY-MM-DD HH:mm:ss.SSS');

        try {
            const response = await axios.get(`/api/log?ut=${updateTime}`);
            if (await apiError(response, standard)) return;

            if (lastLog) {
                const foundIndex = response.data.response.findIndex(({id}) => id === lastLog.id);
                logs.value = [
                    ...logs.value,
                    ...(0 <= foundIndex
                        ? response.data.response.slice(foundIndex + 1)
                        : response.data.response),
                ];
            } else {
                logs.value = response.data.response;
            }

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const clear = () => {
        devices.value = [];
        remocons.value = [];
        schedules.value = [];
        tasks.value = [];
        posts.value = [];
        logs.value = [];
        links.value = [];
    }

    const loadLinks = async () => {
        try {
            const response = await axios.get(`/api/setting/links`);
            if (await apiError(response, standard)) return;

            links.value = response.data.response;

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    const loadTelegramChatIds = async () => {
        try {
            const response = await axios.get(`/api/telegram/user`);
            if (await apiError(response, standard)) return;

            telegramChatIds.value = response.data.response
                .map(item => ({
                    display: item.title
                        ? `[${item.id}] ${item.title}`
                        : `[${item.id}] ${[item.last_name, item.first_name, item.username && `(${item.username})`, ].filter(v => v).join('')}`,
                    ...item,
                }));

            return response;

        } catch (error) {
            console.log(error);
        }
    };

    return {
        connect, disconnect,
        connectWs, disconnectWs,
        devices, loadDevices,
        users, loadUsers,
        remocons, loadRemocons,
        schedules, loadSchedules,
        tasks, loadTasks,
        posts, loadPosts,
        logs, loadLogs,
        links, loadLinks,
        telegramChatIds, loadTelegramChatIds,
        clear,
    };
});
